import OverviewTableColumnRenderer from './OverviewTableColumnRenderer';
import iconRenderer from './iconRenderer';

export default class OverviewTableSubTableRenderer {
    /**
     * @param {Array} columns
     * @param {Array} data
     * @param {Boolean} showContextMenuButton
     * @param {String} evenOddClass 'even' or 'odd' - the color of the parent row this sub-table belongs to, applied
     *                               uniformly to every row (header included) so the whole sub-table reads as part
     *                               of that row instead of zebra-striping on its own.
     *
     * @return {Element}
     */
    static render(columns, data, showContextMenuButton = false, evenOddClass) {
        const div = document.createElement('div');
        div.className = 'overview__table';

        const table = document.createElement('table');
        div.appendChild(table);
        table.className = 'table';

        table.appendChild(this.getTableHeader(columns, showContextMenuButton, evenOddClass));
        table.appendChild(this.getTableBody(columns, data, showContextMenuButton, evenOddClass));

        return div;
    }

    /**
     * Renders one or more sub-table groups stacked underneath each other (e.g. "invoice lines" followed by
     * "insurance details" for the same row).
     *
     * @param {Array<{title: ?String, columns: Array, data: Array}>} groups
     * @param {Boolean} showContextMenuButton
     * @param {String} evenOddClass 'even' or 'odd' - the color of the parent row, see {@link render}.
     *
     * @return {Element}
     */
    static renderGroups(groups, showContextMenuButton = false, evenOddClass) {
        const container = document.createElement('div');
        container.className = 'overview__sub-table-groups';

        groups.filter((group) => group.data.length > 0).forEach((group) => {
            container.appendChild(this.render(group.columns, group.data, showContextMenuButton, evenOddClass));
        });

        return container;
    }

    /**
     * @param {Array} columns
     * @param {Boolean} showContextMenuButton
     * @param {String} evenOddClass 'even' or 'odd' - the color of the parent row, see {@link render}.
     *
     * @return {Element}
     */
    static getTableHeader(columns, showContextMenuButton, evenOddClass) {
        const header = document.createElement('thead');
        const tr = header.insertRow();

        if (evenOddClass) {
            tr.className = evenOddClass;
        }

        columns.forEach((column) => {
            const th = document.createElement('th');
            tr.appendChild(th);

            if (column.options.width) {
                th.style = `width: ${column.options.width}${/^\d+$/.test(column.options.width) ? 'px' : ''};`;
            }

            const span = document.createElement('span');
            th.appendChild(span);
            span.className = 'table__th-wrapper';
            span.innerText = column.label;
        });

        if (showContextMenuButton) {
            const th = document.createElement('th');
            tr.appendChild(th);
            th.className = 'table__context-menu-button-col';

            const span = document.createElement('span');
            th.appendChild(span);

            span.className = 'table__th-wrapper';
        }

        return header;
    }

    /**
     * @param {Array} columns
     * @param {Array} data
     * @param {Boolean} showContextMenuButton
     * @param {String} evenOddClass 'even' or 'odd' - the color of the parent row, see {@link render}. Applied to
     *                               every row so the sub-table doesn't zebra-stripe independently of its parent.
     *
     * @return {Element}
     */
    static getTableBody(columns, data, showContextMenuButton = false, evenOddClass) {
        const body = document.createElement('tbody');

        data.forEach((row) => {
            const tr = body.insertRow();
            tr.dataset.is_subtable_row = '1';
            tr.dataset.subtable_row_data = JSON.stringify(row);

            if (evenOddClass) {
                tr.className = evenOddClass;
            }

            columns.forEach((column) => {
                const td = document.createElement('td');
                tr.appendChild(td);
                td.className = `overview__column overview__column--${column.type}`;
                td.innerHTML = OverviewTableColumnRenderer.render(column.type, row[column.data], column.options);
            });

            if (showContextMenuButton) {
                const td = document.createElement('td');
                tr.appendChild(td);
                td.className = 'overview__column--actions-button';

                const icon = iconRenderer('dots', 'button__icon');
                const button = document.createElement('button');
                button.type = 'button';
                button.className = 'button button--icon button--secondary js-overview-context-menu-button';

                button.innerHTML = icon;

                td.appendChild(button);
            }
        });

        return body;
    }
}
